// Club stand board for a group: a shared map of the club's stands, reserving a stand for a date, who is on which
// stand at a glance, and a stand draw (passiarvonta). Every change is an encrypted group message
// ("stand", "res", and a weekly "clubstate" summary so members who join later get the board even though the server
// deletes messages after 30 days). Each phone builds the board from these; if two people reserve the same stand
// at once, the earlier one wins.
import type { Group, GroupMessage, MessageType, Payload, Person, Reservation, Sender, Stand } from "../../shared/model.ts";
import { $, el, icon, toast } from "../dom.ts";
import { LOCALE, dFmt, pinTypeLabel, t } from "../i18n.ts";
import { FINLAND, hasLeaflet, lastLoc, layerName, tileLayer } from "../map/layers.ts";
import { pins } from "../map/places.ts";
import { dayKey, uid } from "../util.ts";
import { groupMessages, groups, me, send, syncGroup } from "./store.ts";
import { FREE_STANDS } from "../config.ts";
import { billingOff } from "../billing.ts";
import { openPaywall } from "../paywall.ts";
import { clubOpen, refreshClub } from "./clubplan.ts";
import { renderGroups } from "./ui.ts";
import { openSignIn, signedIn } from "../account.ts";
import { AREA_ID_RE, areaOffered, loadClubArea, pointLabel, type AreaPoint, type AreaResult } from "./clubarea.ts";

const CLUB_TYPES: readonly MessageType[] = ["stand", "res", "clubstate", "area"];
export const isClubType = (type: MessageType): type is "stand" | "res" | "clubstate" | "area" => CLUB_TYPES.includes(type);

type StandEntry = Stand & { addedBy?: string };
interface ClubState {
  stands: Map<string, StandEntry>;
  /** Reservations by "standId|date". */
  res: Map<string, Reservation>;
  /** When the newest summary was posted. */
  lastState: number;
  /** Your reservations someone else got first. */
  lost: { standId: string; date: string; holder: string }[];
  /** The Oma riista area identifier, or "" for none. */
  areaId: string;
}

/** Builds the board from the group's messages, oldest first. */
export function foldClub(msgs: GroupMessage[]): ClubState {
  const stands = new Map<string, StandEntry>(), res = new Map<string, Reservation>(), removed = new Set<string>();
  const lost: ClubState["lost"] = [];
  let lastState = 0, areaId: string | null = null, stateArea = "";
  const reserve = (standId: string, date: string, who: Person, ts: number, mine: boolean) => {
    const k = standId + "|" + date, cur = res.get(k);
    if (cur && cur.by !== who.id && cur.ts <= ts) { if (mine) lost.push({ standId, date, holder: cur.byName }); return; }
    res.set(k, { standId, date, by: who.id, byName: who.name, ts });
  };
  for (const m of msgs) {
    if (m.type === "stand") {
      if (m.op === "add" && !removed.has(m.stand.id)) stands.set(m.stand.id, { ...m.stand, addedBy: m.name });
      if (m.op === "remove") { stands.delete(m.stand.id); removed.add(m.stand.id); }
    } else if (m.type === "res" && m.standId && m.date) {
      const who = m.for?.id ? m.for : { id: m.from, name: m.name };
      if (m.op === "reserve") reserve(m.standId, m.date, who, m.ts, m.from === me.id);
      if (m.op === "cancel") {
        const k = m.standId + "|" + m.date, cur = res.get(k);
        if (cur && (cur.by === who.id || m.from === cur.by || m.for)) res.delete(k);
      }
    } else if (m.type === "area") {
      areaId = m.areaId ?? "";
    } else if (m.type === "clubstate") {
      lastState = Math.max(lastState, m.ts);
      if (m.area) stateArea = m.area;
      for (const s of m.stands ?? []) if (!stands.has(s.id) && !removed.has(s.id)) stands.set(s.id, s);
      for (const r of m.res ?? []) { const k = r.standId + "|" + r.date; if (!res.has(k)) res.set(k, r); }
    }
  }
  return { stands, res, lastState, lost, areaId: areaId ?? stateArea };
}

const dlg = $<HTMLDialogElement>("#club");
let clubGroupId: string | null = null, clubDate = dayKey(Date.now()), clubState: ClubState | null = null, clubTimer = 0;
let clubMap: L.Map | null = null, clubLayer: L.LayerGroup | null = null, areaLayer: L.LayerGroup | null = null;
/** The area drawn on the map now ("" = none), so it is drawn and fitted to once. */
let drawnArea = "", fitAreaNext = false;
const shownLost = new Set<string>();
const clubGroup = () => groups.find((g) => g.id === clubGroupId);

export async function openClub(groupId: string): Promise<void> {
  clubGroupId = groupId;
  clubDate = dayKey(Date.now());
  const g = clubGroup();
  if (!g) return;
  $("#club-title").textContent = t("clubTitle", g.name);
  if (!dlg.open) dlg.showModal();
  if (!clubMap && hasLeaflet()) {
    clubMap = L.map("club-map", { zoomControl: !matchMedia("(pointer: coarse)").matches, attributionControl: false });
    tileLayer(layerName).addTo(clubMap);
    areaLayer = L.layerGroup().addTo(clubMap);
    clubLayer = L.layerGroup().addTo(clubMap);
  }
  drawnArea = "";
  await renderClub(true);
  void refreshClub(g).then(() => { if (dlg.open && clubGroupId === g.id) void renderClub(false); });
  void maybePostClubState();
  clearInterval(clubTimer);
  clubTimer = window.setInterval(async () => { if (dlg.open && document.visibilityState === "visible" && (await syncGroup(g))) void renderClub(false); }, 5000);
}
$("#club-close").addEventListener("click", () => { clearInterval(clubTimer); if (dlg.open) dlg.close(); });
dlg.addEventListener("close", () => clearInterval(clubTimer));

// The next 7 days as chips; reservations are per day.
function drawDates(): void {
  const chips: HTMLElement[] = [];
  for (let i = 0; i < 7; i++) {
    const ts = Date.now() + i * 864e5, k = dayKey(ts);
    const label = i === 0 ? t("when_day_0") : i === 1 ? t("when_day_1") : dFmt.format(ts);
    chips.push(el("button", { class: "chip", type: "button", "aria-pressed": String(k === clubDate), onclick: () => { clubDate = k; void renderClub(false); } }, label));
  }
  $("#club-dates").replaceChildren(...chips);
}
const initials = (name: string) => (name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

async function renderClub(fit: boolean): Promise<void> {
  const g = clubGroup();
  if (!g) return;
  const state = foldClub(await groupMessages(g.id));
  clubState = state;
  // tell the user once if someone else got a stand first
  for (const l of state.lost) { const k = l.standId + l.date; if (!shownLost.has(k)) { shownLost.add(k); toast(t("clubLost", l.holder)); } }
  drawDates();
  const stands = [...state.stands.values()].sort((a, b) => (a.name || "").localeCompare(b.name || "", LOCALE, { numeric: true }));
  const resFor = (s: Stand) => state.res.get(s.id + "|" + clubDate);
  // map
  if (clubMap && clubLayer) {
    const map = clubMap, layer = clubLayer;
    layer.clearLayers();
    for (const s of stands) {
      const r = resFor(s), cls = r ? (r.by === me.id ? "mine" : "taken") : "free";
      const d = el("div", { class: "cstand " + cls }, el("span", { text: r ? initials(r.byName) : "" }));
      L.marker([s.lat, s.lon], { icon: L.divIcon({ html: d, className: "", iconSize: [34, 34], iconAnchor: [17, 17] }), title: s.name })
        .addTo(layer).on("click", () => document.getElementById("cs-" + s.id)?.scrollIntoView({ behavior: "smooth", block: "center" }));
    }
    requestAnimationFrame(() => {
      map.invalidateSize();
      if (!fit) return;
      const last = lastLoc();
      if (stands.length) map.fitBounds(stands.map((s): L.LatLngTuple => [s.lat, s.lon]), { padding: [30, 30], maxZoom: 15 });
      else if (!state.areaId) map.setView(last ?? FINLAND, last ? 11 : 5); // with an area, drawArea zooms to it
    });
  }
  drawPlan(g, stands.length);
  void drawArea(state, fit && !stands.length);
  // summary + list
  const taken = stands.filter((s) => resFor(s)).length;
  $("#club-summary").textContent = stands.length ? t("clubSummary", taken, stands.length) : "";
  if (!stands.length) { $("#club-list").replaceChildren(el("div", { class: "empty" }, icon("stand"), el("p", { text: t("clubEmpty") }))); return; }
  $("#club-list").replaceChildren(...stands.map((s) => {
    const r = resFor(s), mine = r?.by === me.id;
    const status = r ? (mine ? t("clubMine") : t("clubTakenBy", r.byName)) : t("clubFree");
    const act = !r ? el("button", { class: "btn primary small", type: "button", onclick: () => void clubSend({ type: "res", op: "reserve", standId: s.id, date: clubDate }) }, t("clubReserve"))
      : mine ? el("button", { class: "btn small", type: "button", onclick: () => void clubSend({ type: "res", op: "cancel", standId: s.id, date: clubDate }) }, t("clubCancel"))
      : el("button", { class: "linkbtn", type: "button", onclick: (e) => armTwice(e.currentTarget, () => void clubSend({ type: "res", op: "cancel", standId: s.id, date: clubDate, for: { id: r.by, name: r.byName } }), true) }, t("clubFreeIt"));
    return el("div", { class: "crow " + (r ? (mine ? "mine" : "taken") : "free"), id: "cs-" + s.id },
      el("span", { class: "cdot", "aria-hidden": "true", text: r ? initials(r.byName) : "" }),
      el("div", { class: "cmain" }, el("b", { text: s.name || t("clubStand") }), el("small", { text: status })),
      act,
      el("button", { class: "iconbtn small", type: "button", "aria-label": t("clubRemove", s.name), onclick: (e) => armTwice(e.currentTarget, () => void clubSend({ type: "stand", op: "remove", stand: { id: s.id } }), false) }, icon("close")));
  }));
}

/* ---------- the club's hunting area from Oma riista (clubarea.ts) ---------- */
const POI_COLORS: Record<string, string> = { SIGHTING_PLACE: "--blaze", FEEDING_PLACE: "--pine", MINERAL_LICK: "--lake" };
const cssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
const poiLabel = (type: string) => t(type === "SIGHTING_PLACE" ? "poiStand" : type === "FEEDING_PLACE" ? "poiFeeding" : type === "MINERAL_LICK" ? "poiLick" : "poiOther");
const areaError = (e: string, id: string) => (e === "notfound" ? t("areaNotFound", id) : e === "offline" ? t("areaOffline") : t("areaUnavailable"));
/** Within about 10 m: that stand is already on the board. */
const near = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => Math.abs(a.lat - b.lat) < 1e-4 && Math.abs(a.lon - b.lon) < 2e-4;

async function drawArea(state: ClubState, fitToArea: boolean): Promise<void> {
  const box = $("#club-area"), id = state.areaId;
  if (!id) {
    areaLayer?.clearLayers(); drawnArea = "";
    box.replaceChildren(...(areaOffered() ? [el("button", { class: "linkbtn", type: "button", onclick: () => openAreaForm("") }, t("areaAdd"))] : []));
    return;
  }
  if (drawnArea !== id) box.replaceChildren(el("p", { class: "hint", text: t("areaLoading") }));
  const r: AreaResult = await loadClubArea(id);
  if (clubState?.areaId !== id) return; // the area changed meanwhile
  const change = el("button", { class: "linkbtn", type: "button", onclick: () => openAreaForm(id) }, t("areaChange"));
  if ("error" in r) {
    areaLayer?.clearLayers(); drawnArea = "";
    box.replaceChildren(r.error === "signin"
      ? el("div", { class: "area-row" }, el("p", { class: "hint", text: t("areaSignIn") }),
        el("button", { class: "btn small", type: "button", onclick: () => openSignIn(() => void renderClub(true)) }, t("accountSignIn")))
      : el("div", { class: "area-row" }, el("p", { class: "hint warn", text: areaError(r.error, id) }), change));
    return;
  }
  const a = r.area, stands = [...state.stands.values()];
  const newStands = a.points.filter((p) => p.type === "SIGHTING_PLACE" && !stands.some((s) => near(s, p)));
  box.replaceChildren(el("div", { class: "area-row" },
    el("div", { class: "cmain" }, el("b", { text: a.name || t("areaTitle") }), el("small", { text: [a.club, a.year ? t("areaYear", a.year, a.year + 1) : ""].filter(Boolean).join(" · ") })),
    change),
    ...(newStands.length ? [el("button", { class: "btn small", type: "button", style: "align-self:flex-start", onclick: () => void importStands(newStands) }, icon("stand"), el("span", { text: t("areaImport", newStands.length) }))] : []));
  if (!clubMap || !areaLayer || drawnArea === id) return;
  areaLayer.clearLayers();
  const outline = L.geoJSON(a.geo, { style: { color: cssVar("--blaze"), weight: 2.5, fillOpacity: 0.06, interactive: false } }).addTo(areaLayer);
  for (const p of a.points) {
    L.circleMarker([p.lat, p.lon], { radius: 5, color: "#fff", weight: 1.5, fillColor: cssVar(POI_COLORS[p.type] ?? "--muted"), fillOpacity: 1 })
      .bindTooltip([pointLabel(p) || poiLabel(p.type), p.notes].filter(Boolean).join(" – ")).addTo(areaLayer);
  }
  drawnArea = id;
  // zoom to the area when the board opens without stands, and right after the area is set
  if (fitToArea || fitAreaNext) {
    fitAreaNext = false;
    const map = clubMap, b = outline.getBounds();
    if (b.isValid()) requestAnimationFrame(() => { map.invalidateSize(); map.fitBounds(b, { padding: [20, 20] }); });
  }
}

/** Adds the area's stands (Oma riista "passi" points) to the board, as many as the plan allows. */
async function importStands(points: AreaPoint[]): Promise<void> {
  const room = freeRoom();
  if (room <= 0) { askClubPlan(t("payWhyStands", FREE_STANDS)); return; }
  for (const p of points.slice(0, room)) await clubSend({ type: "stand", op: "add", stand: { id: uid(), name: pointLabel(p) || t("clubStand"), lat: p.lat, lon: p.lon } });
  if (points.length > room) askClubPlan(t("payWhyStands", FREE_STANDS));
  else toast(t("areaImported", points.length));
}

/** Setting, changing or removing the area identifier. It is checked with Oma riista before the group gets it. */
function openAreaForm(current: string): void {
  if (!signedIn()) { openSignIn(() => openAreaForm(current)); return; }
  const msg = el("p", { class: "hint warn", hidden: true });
  const field = el("input", { type: "text", id: "club-areaid", value: current, autocomplete: "off", autocapitalize: "characters", spellcheck: "false", maxlength: "40", placeholder: "7YUGZCTATZ" });
  const save = el("button", { class: "btn primary small", type: "button", onclick: async () => {
    const id = field.value.replace(/\s+/g, "");
    if (!AREA_ID_RE.test(id)) { msg.textContent = t("areaBadId"); msg.hidden = false; field.focus(); return; }
    save.disabled = true;
    const r = await loadClubArea(id, true);
    save.disabled = false;
    if ("error" in r) { msg.textContent = areaError(r.error, id); msg.hidden = false; return; }
    closeAddBox();
    drawnArea = ""; fitAreaNext = true;
    if (id !== current) await clubSend({ type: "area", areaId: id });
    else void renderClub(true);
  } }, t("areaSave"));
  addBox().replaceChildren(el("div", { class: "f" }, el("span", { class: "lbl", text: t("areaIdLbl") }),
    el("div", { class: "addrow" }, field, save), msg,
    el("p", { class: "hint", text: t("areaIdHint") }),
    current ? el("button", { class: "linkbtn", type: "button", style: "align-self:flex-start", onclick: (e) => armTwice(e.currentTarget, () => { closeAddBox(); void clubSend({ type: "area", areaId: "" }); }, true) }, t("areaRemove")) : null));
  addBox().hidden = false;
  $("#club-crosshair").hidden = true;
  setTimeout(() => field.focus(), 50);
}

/* ---------- the club plan: without it, FREE_STANDS (3) stands and no draw; stands and reservations always stay visible ---------- */
function drawPlan(g: Group, standCount: number): void {
  const box = $("#club-plan");
  box.hidden = billingOff();
  if (box.hidden) return;
  box.replaceChildren(...(clubOpen(g)
    ? [el("span", { class: "plus-tag" }, icon("check"), t("clubPlanOn"))]
    : [el("span", { class: "num", text: t("clubFreeCount", Math.min(standCount, FREE_STANDS), FREE_STANDS) }),
      el("button", { class: "btn small", type: "button", onclick: () => askClubPlan() }, t("clubUnlock"))]));
}
/** The club plan screen; afterwards the board redraws. */
function askClubPlan(reason?: string): void {
  const g = clubGroup();
  if (g) openPaywall("club", { group: g, reason, onDone: () => void renderClub(false) });
}
/** How many more stands fit without the club plan (Infinity with it). */
function freeRoom(): number {
  const g = clubGroup();
  return !g || clubOpen(g) ? Infinity : Math.max(0, FREE_STANDS - (clubState?.stands.size ?? 0));
}

/** Two taps for anything that affects other members. `relabel`: show "Confirm?" on the first tap. */
function armTwice(btn: HTMLElement, fn: () => void, relabel: boolean): void {
  if (btn.dataset.armed) { delete btn.dataset.armed; fn(); return; }
  btn.dataset.armed = "1";
  btn.classList.add("armed");
  const old = btn.innerHTML;
  if (relabel) btn.textContent = t("clubConfirm");
  setTimeout(() => { delete btn.dataset.armed; btn.classList.remove("armed"); if (relabel) btn.innerHTML = old; }, 3500);
}
async function clubSend(payload: Payload): Promise<void> {
  const g = clubGroup();
  if (!g) return;
  try { await send(g, payload); await syncGroup(g); await renderClub(false); renderGroups(); }
  catch (e) { console.error(e); toast(t("msgNotSent")); }
}

/* ---------- adding stands: from your own stand pins, or at the middle of the club map ---------- */
const addBox = () => $("#club-add");
function closeAddBox(): void { addBox().hidden = true; $("#club-crosshair").hidden = true; }
function openAddStands(): void {
  if (addBox().hidden && freeRoom() <= 0) { askClubPlan(t("payWhyStands", FREE_STANDS)); return; }
  const have = new Set([...(clubState?.stands.values() ?? [])].map((s) => s.lat.toFixed(5) + s.lon.toFixed(5)));
  const mine = pins.filter((p) => p.type === "stand" && !have.has(p.lat.toFixed(5) + p.lon.toFixed(5)));
  const boxes = mine.map((p) => el("label", { class: "toggle" }, el("input", { type: "checkbox", "data-id": p.id, checked: true }), el("span", { text: p.name || pinTypeLabel(p.type) })));
  addBox().replaceChildren(
    mine.length ? el("div", { class: "f" }, el("span", { class: "lbl", text: t("clubFromPins") }), ...boxes,
      el("button", { class: "btn primary small", type: "button", onclick: async () => {
        const ids = [...addBox().querySelectorAll<HTMLInputElement>("input[data-id]:checked")].map((i) => i.dataset.id);
        const chosen = mine.filter((x) => ids.includes(x.id)), room = freeRoom();
        for (const p of chosen.slice(0, room)) await clubSend({ type: "stand", op: "add", stand: { id: uid(), name: p.name || t("clubStand"), lat: p.lat, lon: p.lon } });
        closeAddBox();
        if (chosen.length > room) askClubPlan(t("payWhyStands", FREE_STANDS));
      } }, t("clubAddChosen"))) : el("p", { class: "hint", text: t("clubNoOwnStands") }),
    el("div", { class: "f" }, el("span", { class: "lbl", text: t("clubAtCenter") }),
      el("div", { class: "addrow" }, el("input", { type: "text", id: "club-newname", placeholder: t("clubNamePh"), autocomplete: "off" }),
        el("button", { class: "btn small", type: "button", onclick: async () => {
          const field = $<HTMLInputElement>("#club-newname"), name = field.value.trim();
          if (!name) { field.focus(); return; }
          if (freeRoom() <= 0) { closeAddBox(); askClubPlan(t("payWhyStands", FREE_STANDS)); return; }
          const c = clubMap?.getCenter();
          if (!c) return;
          await clubSend({ type: "stand", op: "add", stand: { id: uid(), name, lat: c.lat, lon: c.lng } });
          closeAddBox();
        } }, t("clubAdd")))));
  addBox().hidden = !addBox().hidden;
  $("#club-crosshair").hidden = addBox().hidden;
}
$("#club-addbtn").addEventListener("click", openAddStands);

/* ---------- stand draw: shuffle the hunters who are coming onto the free stands for the chosen day ---------- */
async function openDraw(): Promise<void> {
  const g = clubGroup(), state = clubState;
  if (!g || !state) return;
  if (!clubOpen(g)) { askClubPlan(t("payWhyDraw")); return; } // the stand draw is part of the club plan
  const people = new Map<string, string>();
  for (const m of await groupMessages(g.id)) if (m.from && m.name) people.set(m.from, m.name);
  const free = [...state.stands.values()].filter((s) => !state.res.get(s.id + "|" + clubDate));
  const boxes = [...people].map(([id, name]) => el("label", { class: "toggle" }, el("input", { type: "checkbox", "data-id": id, "data-name": name, checked: true }), el("span", { text: name })));
  addBox().replaceChildren(el("div", { class: "f" }, el("span", { class: "lbl", text: t("drawWho", free.length) }), ...boxes,
    el("button", { class: "btn primary small", type: "button", onclick: async () => {
      const who: Person[] = [...addBox().querySelectorAll<HTMLInputElement>("input[data-id]:checked")].map((i) => ({ id: i.dataset.id ?? "", name: i.dataset.name ?? "" }));
      const already = new Set([...state.res.values()].filter((r) => r.date === clubDate).map((r) => r.by));
      const hunters = who.filter((w) => !already.has(w.id));
      if (!hunters.length || !free.length) { toast(t("drawNothing")); return; }
      // Fisher–Yates with the crypto random generator, so nobody can predict the draw
      const shuffled = [...free];
      for (let i = shuffled.length - 1; i > 0; i--) { const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1); [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; }
      for (let i = 0; i < Math.min(hunters.length, shuffled.length); i++) await clubSend({ type: "res", op: "reserve", standId: shuffled[i].id, date: clubDate, for: hunters[i] });
      closeAddBox();
      toast(hunters.length > shuffled.length ? t("drawTooFew", shuffled.length) : t("drawDone"));
    } }, t("drawGo"))));
  addBox().hidden = false;
  $("#club-crosshair").hidden = true;
}
$("#club-draw").addEventListener("click", () => void openDraw());

// A summary every 7 days keeps the board alive for new members after the server has deleted old messages.
async function maybePostClubState(): Promise<void> {
  const g = clubGroup(), state = clubState;
  if (!g || !(state?.stands.size || state?.areaId) || Date.now() - state.lastState < 7 * 864e5) return;
  const today = dayKey(Date.now());
  await send(g, { type: "clubstate", stands: [...state.stands.values()].map(({ id, name, lat, lon }) => ({ id, name, lat, lon })),
    res: [...state.res.values()].filter((r) => r.date >= today), ...(state.areaId ? { area: state.areaId } : {}) }).catch(() => {});
}

/** A short line in the chat for a club change (null for the invisible summary). `stands` maps ids to names, when known. */
export function clubEventText(m: Payload & Sender, stands?: Map<string, StandEntry>): string | null {
  if (m.type === "stand") return m.op === "add" ? t("evStandAdded", m.name, m.stand.name || "") : t("evStandRemoved", m.name);
  if (m.type === "area") return m.areaId ? t("evAreaSet", m.name) : t("evAreaRemoved", m.name);
  if (m.type === "res") {
    const date = m.date ? dFmt.format(new Date(m.date + "T12:00")) : "";
    const who = m.for?.name || m.name, stand = stands?.get(m.standId)?.name || t("clubStand");
    if (m.op === "cancel") return t("evCancelled", who, stand, date);
    return m.for && m.for.id !== m.from ? t("evDrawn", who, stand, date) : t("evReserved", who, stand, date);
  }
  return null;
}
